import { LandingBracketSectionCoverMarquee } from "@landing/components/landing/LandingBracketSectionCoverMarquee"
import {
  fightSongTracks,
  getClipForSport,
  introBaseballClip,
  introBasketballClip,
  introFootballClip,
  introGolfClip,
  introHockeyClip,
  introTennisClip,
  playSingleAudio,
} from "@landing/components/landing/LandingSounds"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowDown, VolumeX } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"
import { TypeAnimation } from "react-type-animation"
import { LandingIconAnimatedActivity } from "./LandingIconAnimatedActivity"
import { LandingLogoLiveTheGame } from "./LandingLogoLiveTheGame"
import { HOW_BRACKETS_WORK_FAQ_ID } from "./LandingSectionFAQ"

// ------------------------- Headline helpers -------------------------
const leagueMapping: Record<string, string> = {
  football: "NFL",
  basketball: "NBA",
  baseball: "MLB",
  hockey: "NHL",
  soccer: "Soccer",
  golf: "Golf",
  tennis: "Tennis",
  boxing: "UFC",
}

const leagueKeywords: Record<string, string[]> = {
  NFL: ["Drives", "Moments", "Matchups"],
  NBA: ["Buckets", "Series", "Moments", "Matchups"],
  MLB: ["Swings", "Rallies", "Swings", "At Bats", "Saves", "Series", "Moments"],
  NHL: ["Saves", "Goals", "Faceoffs", "Series", "Moments"],
  Soccer: ["Goals", "Kicks", "Matches", "Moments"], // formerly UEFA
  Golf: ["Rounds", "Majors", "Events", "Moments"],
  Tennis: ["Sets", "Swings", "Aces", "Rallies", "Matches"], // formerly ATP
  UFC: ["Fights", "Knockouts", "Events"],
  CFB: ["Rivalries", "Upsets", "Underdogs", "Matchups", "Moments"],
}

const mobileExcludedKeywords = new Set([
  "Matchups",
  "Rivalries",
  "Takedowns",
  "Submissions",
  "Knockouts",
  "Comebacks",
  "4th Downs",
  "Hail Marys",
])

const desktopExcludedKeywords = new Set(["KOs", "TDs", "INTs"])

function getRandomKeyword(lg: string, prev?: string): string {
  let list = leagueKeywords[lg] ?? ["Moments"]
  if (typeof window !== "undefined") {
    if (window.innerWidth < 640) {
      list = list.filter((w) => !mobileExcludedKeywords.has(w))
    } else {
      list = list.filter((w) => !desktopExcludedKeywords.has(w))
    }
  }
  if (prev && list.length > 1) list = list.filter((w) => w !== prev)
  if (list.length === 0) list = ["Moments"]
  const keyword = list[Math.floor(Math.random() * list.length)]
  return keyword ?? "Moments"
}

// (Removed word-by-word heading animation)

const sportsEmojis = [
  // NFL / CFB (Football)
  { emoji: "https://a.bracky.app/assets/landing/emojis/football-900.png", name: "football", isImage: true },
  // NBA (Basketball)
  { emoji: "https://a.bracky.app/assets/landing/emojis/basketball-6.webp", name: "basketball", isImage: true },
  // MLB (Baseball)
  { emoji: "https://a.bracky.app/assets/landing/emojis/baseball-2.webp", name: "baseball", isImage: true },
  // UEFA (Soccer)
  { emoji: "https://a.bracky.app/assets/landing/emojis/soccer.png", name: "soccer", isImage: true },
  // Golf
  { emoji: "https://a.bracky.app/assets/landing/emojis/golf-21.png", name: "golf", isImage: true },
  // NHL (Hockey)
  { emoji: "https://a.bracky.app/assets/landing/emojis/hockey-32.png", name: "hockey", isImage: true },
  // UFC (Boxing/MMA)
  { emoji: "https://a.bracky.app/assets/landing/emojis/boxing-892.png", name: "boxing", isImage: true },
  // ATP (Tennis)
  { emoji: "https://a.bracky.app/assets/landing/emojis/tennis-20.webp", name: "tennis", isImage: true },
]

// ---- Headline gradient colors and inline component ----
const sportGradients: Record<string, string> = {
  Tennis: "from-[#75E619] to-[#DDFF31]",
  UFC: "from-[#FF2D31] to-[#FF568F]",
  NHL: "from-[#5020FF] to-[#1EF8FF]",
  Golf: "from-[#00C932] to-[#95FF4F]",
  Soccer: "from-[#9298B4] to-[#CAD5E7]",
  MLB: "from-[#FF4422] to-[#FFCB53]",
  NBA: "from-[#FFC300] to-[#FF6600]",
  NFL: "from-[#0022FF] to-[#12E7E7]",
  CFB: "from-[#0022FF] to-[#12E7E7]",
}

function CaretBuy({ className = "" }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 57 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Buy caret"
      className={className}
      style={{ display: "block" }}
      initial={{ y: 6, filter: "drop-shadow(0 0 4px rgba(34,255,101,0.6))" }}
      animate={{
        y: 6,
        scale: [1, 0.92, 1.01, 1],
        opacity: [1, 0.75, 1, 1],
        filter: [
          "drop-shadow(0 0 4px rgba(34,255,101,0.6))",
          "drop-shadow(0 0 4px rgba(34,255,101,0))",
          "drop-shadow(0 0 4px rgba(34,255,101,0.6))",
          "drop-shadow(0 0 4px rgba(34,255,101,0.6))",
        ],
      }}
      transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
    >
      <path d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z" fill="#22FF65" />
    </motion.svg>
  )
}

function CaretSell({ className = "" }: { className?: string }) {
  return (
    <motion.svg
      viewBox="0 0 57 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Sell caret"
      className={className}
      style={{ display: "block" }}
      initial={{ rotate: 180, y: 6, filter: "drop-shadow(0 0 4px rgba(255,45,85,0.6))" }}
      animate={{
        y: 6,
        rotate: 180,
        scale: [1, 0.92, 1.04, 1],
        opacity: [1, 0.75, 1, 1],
        filter: [
          "drop-shadow(0 0 4px rgba(255,45,85,0.6))",
          "drop-shadow(0 0 4px rgba(255,45,85,0))",
          "drop-shadow(0 0 4px rgba(255,45,85,0.6))",
          "drop-shadow(0 0 4px rgba(255,45,85,0.6))",
        ],
      }}
      transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
    >
      <path d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z" fill="#FF2D55" />
    </motion.svg>
  )
}

interface BracketHeadlineProps {
  leagueLabel: string
  keyword: string
  animationKey: string | number
}

function BracketHeadline({ leagueLabel, keyword, animationKey }: BracketHeadlineProps) {
  const isDimmed = false
  const greenFilterId = useId()
  const redFilterId = useId()
  const greenFilterMediumId = useId()
  const redFilterMediumId = useId()
  const greenFilterMobileId = useId()
  const redFilterMobileId = useId()

  const TYPE_SPEED_MS = 90

  const leagueWord = (
    <TypeAnimation
      key={`league-${animationKey}`}
      sequence={[`${leagueLabel}'s`]}
      speed={{ type: "keyStrokeDelayInMs", value: TYPE_SPEED_MS }}
      preRenderFirstString={false}
      omitDeletionAnimation
      cursor={false}
      wrapper="span"
      repeat={0}
      className={`font-medium inline-block align-baseline bg-linear-to-r ${sportGradients[leagueLabel] || "from-white via-[#30DDFF] to-[#22FF65]"} bg-clip-text text-transparent transition-opacity duration-200 ${isDimmed ? "opacity-50" : "opacity-100"}`}
      style={{
        lineHeight: "1em",
        height: "1em",
        minWidth: "0.6ch",
        contain: "layout",
        transform: "translateY(0.14em)",
        backgroundSize: "100% 300%",
        backgroundPosition: "0 -50%",
      }}
    />
  )

  const keywordWord = (
    <TypeAnimation
      key={`keyword-${animationKey}`}
      sequence={[keyword]}
      speed={{ type: "keyStrokeDelayInMs", value: TYPE_SPEED_MS }}
      preRenderFirstString={false}
      omitDeletionAnimation
      cursor={false}
      wrapper="span"
      repeat={0}
      className={`font-extrabold inline-block transition-opacity sm:translate-y-[.88rem] duration-200 ${isDimmed ? "opacity-50" : "opacity-100"} bg-linear-to-b from-white to-[#dce3fe] bg-clip-text text-transparent`}
      style={{ lineHeight: "1em", height: "1.1em", minWidth: "0.6ch", contain: "layout", overflow: "visible" }}
    />
  )

  return (
    <>
      {/* Large screens and up - single line with dynamic keywords */}
      <span className="hidden lg:flex items-center justify-center gap-[0.6rem]">
        <CaretBuy className="w-[1.33rem] h-[1.33rem] shrink-0 relative top-[8px] lg:top-[3px]" />
        <span className="whitespace-nowrap">
          Bet&nbsp;{leagueWord}&nbsp;Biggest&nbsp;{keywordWord}&nbsp;Live
        </span>
        <CaretSell className="w-[1.33rem] h-[1.33rem] shrink-0 relative top-[8px] lg:top-[3px]" />
      </span>

      {/* Medium screens - 3-line layout with "Moments" */}
      <span className="hidden md:flex lg:hidden flex-col items-center text-center gap-[0.7rem] leading-[2.33rem]">
        <span>Bet&nbsp;{leagueWord}</span>
        <span>Biggest&nbsp;Moments</span>
        <span className="flex items-center justify-center gap-2 mt-0">
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[8px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Buy caret"
            role="img"
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1] }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Buy caret</title>
            <defs>
              <filter id={greenFilterId} filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#22FF65"
              filter={`url(#${greenFilterId})`}
            />
          </motion.svg>
          <span className="font-extrabold">Live</span>
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[8px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Sell caret"
            role="img"
            initial={{ rotate: 180 }}
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1], rotate: 180 }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Sell caret</title>
            <defs>
              <filter id={redFilterId} filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#FF2D55"
              filter={`url(#${redFilterId})`}
            />
          </motion.svg>
        </span>
      </span>

      {/* Small screens - 3-line layout with "Moments" */}
      <span className="hidden sm:flex md:hidden flex-col items-center text-center gap-[0.3rem] leading-[2.33rem]">
        <span>Bet&nbsp;{leagueWord}</span>
        <span>Biggest&nbsp;Moments</span>
        <span className="flex items-center justify-center gap-2 mt-0">
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[8px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Buy caret"
            role="img"
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1] }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Buy caret</title>
            <defs>
              <filter
                id={greenFilterMediumId}
                filterUnits="userSpaceOnUse"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#22FF65"
              filter={`url(#${greenFilterMediumId})`}
            />
          </motion.svg>
          <span className="font-extrabold">Live</span>
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[8px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Sell caret"
            role="img"
            initial={{ rotate: 180 }}
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1], rotate: 180 }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Sell caret</title>
            <defs>
              <filter id={redFilterMediumId} filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#FF2D55"
              filter={`url(#${redFilterMediumId})`}
            />
          </motion.svg>
        </span>
      </span>

      {/* Mobile - 3-line layout with "Moments" */}
      <span className="sm:hidden flex flex-col items-center text-center gap-[0.1rem] leading-[2.33rem]">
        <span>Bet&nbsp;{leagueWord}</span>
        <span>Biggest&nbsp;Moments</span>
        <span className="flex items-center justify-center gap-2 mt-0">
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[1.6px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Buy caret"
            role="img"
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1] }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Buy caret</title>
            <defs>
              <filter
                id={greenFilterMobileId}
                filterUnits="userSpaceOnUse"
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#22FF65"
              filter={`url(#${greenFilterMobileId})`}
            />
          </motion.svg>
          <span className="font-extrabold">Live</span>
          <motion.svg
            viewBox="0 0 57 48"
            className="w-[14.4px] h-[14.4px] relative top-[1.6px] lg:top-[3px] lg:scale-[.8]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Sell caret"
            role="img"
            initial={{ rotate: 180 }}
            animate={{ scale: [1, 0.8, 1.03, 1], opacity: [1, 0.85, 1, 1], rotate: 180 }}
            transition={{ duration: 1.44, repeat: Number.POSITIVE_INFINITY, repeatType: "loop", ease: "easeInOut" }}
          >
            <title>Sell caret</title>
            <defs>
              <filter id={redFilterMobileId} filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M56.51 41.28L54.5 47.54H2.01L0 41.28L28.21 0L56.51 41.28Z"
              fill="#FF2D55"
              filter={`url(#${redFilterMobileId})`}
            />
          </motion.svg>
        </span>
      </span>
    </>
  )
}

// (Headline-related constants moved to LandingBracketHeadline.tsx)

// Mapping of sport name to the masked video animation that should play inside the "LIVE THE GAME" logo.
// NOTE: Replace the placeholder URLs with the actual GIF/MP4/WebM sources when they are available.
// Exported so other components (e.g. landing page) can reuse if needed
const sportVideos: Record<string, { mp4: string; webm: string }> = {
  football: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-football.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-football.mp4",
  },
  basketball: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-basketball.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-basketball.mp4",
  },
  soccer: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-soccer.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-soccer.mp4",
  },
  golf: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-golf.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-golf.mp4",
  },
  tennis: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-tennis.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-tennis.mp4",
  },
  baseball: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-baseball.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-baseball.mp4",
  },
  hockey: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-hockey.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-hockey.mp4",
  },
  boxing: {
    mp4: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-boxing.mp4",
    webm: "https://a.bracky.app/assets/landing/backgrounds/live-the-game-boxing.mp4",
  },
}

// We will rely on a single global audio element to avoid overlaps.
declare global {
  interface Window {
    activeCallAudio?: HTMLAudioElement
  }
}

export function LandingSectionBracket() {
  const [_expandedCard, setExpandedCard] = useState<null | "nfl" | "cfb" | "mlb" | "oscars" | "beer">("nfl")
  const [currentEmojiIndex, setCurrentEmojiIndex] = useState(0)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  // Track image loading state for debugging
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set())
  const [imageLoaded, setImageLoaded] = useState<Set<number>>(new Set())
  // Exposed sport name only after its emoji asset is ready to avoid headline re-mount glitch
  const firstEmoji = sportsEmojis[0]
  const [currentSportName, setCurrentSportName] = useState<string>(firstEmoji?.name ?? "football")
  const [_allowBracketInteractions, setAllowBracketInteractions] = useState(true)
  // Track whether the bracket section is currently visible in the viewport
  const [_isBracketSectionVisible, setIsBracketSectionVisible] = useState(false)

  // Headline synchronized data
  const [headlineLeague, setHeadlineLeague] = useState("NFL")
  const [headlineKeyword, setHeadlineKeyword] = useState("Moments")
  const [headlineKey, setHeadlineKey] = useState(0)
  // Headline animation is now encapsulated in <LandingBracketHeadline>

  const headlineLeagueRef = useRef(headlineLeague)
  useEffect(() => {
    headlineLeagueRef.current = headlineLeague
  }, [headlineLeague])

  // Remember last processed emoji to avoid duplicate headline updates
  const lastProcessedEmojiRef = useRef<number>(-1)

  /**
   * Pre-load all masked sport videos (mp4/webm) plus the subtle overlay video so
   * they are cached by the browser before the bracket section scrolls into
   * view. This dramatically reduces – or eliminates – the brief blank period
   * experienced on mobile when the word-mark first appears.
   */
  useEffect(() => {
    // Gather every unique video URL we intend to show.
    const urls: string[] = []

    for (const { mp4, webm } of Object.values(sportVideos)) {
      urls.push(mp4, webm)
    }

    // Overlay texture used by <LandingLogoLiveTheGame>
    const overlayUrl = "https://a.bracky.app/assets/landing/overlays/logo-live-the%20game-blue-screen-shift.mp4"
    urls.push(overlayUrl)

    // ------------------------------------------------------------------
    //  NEW: Pre-load all emoji icons so the headline animation never
    //  receives a late re-render when an <img> finishes downloading.
    //  These files are ~10 kB each, negligible on total page weight.
    // ------------------------------------------------------------------
    for (const { emoji } of sportsEmojis) {
      if (emoji) urls.push(emoji)
    }

    // Inject <link rel="preload"> tags for each source (skip duplicates).
    const injected: HTMLLinkElement[] = []
    for (const href of urls) {
      if (document.querySelector(`link[rel="preload"][href="${href}"]`)) continue

      const link = document.createElement("link")
      link.rel = "preload"
      // Heuristic: treat video extensions as video, otherwise image
      link.as = href.endsWith(".mp4") || href.endsWith(".webm") ? "video" : "image"
      link.href = href
      // Media files are served with CORS headers – set crossOrigin for safety.
      link.crossOrigin = "anonymous"
      document.head.appendChild(link)
      injected.push(link)
    }

    // Clean up on unmount – not strictly necessary but keeps DOM tidy during
    // hot-reload in development.
    return () => {
      for (const lnk of injected) {
        lnk.parentElement?.removeChild(lnk)
      }
    }
  }, [])

  const bobbingDuration = isAudioPlaying ? 2.5 / 1.5 : 2.5

  // Derive the video sources for the logo animation.
  const currentVideoSrc = sportVideos[currentSportName]

  // ------------------------------------------------------------------
  //  Sync headline after the emoji asset is ready *and* a fixed pop-in delay
  //  (400 ms).  This ensures we start typing only after the new icon is fully
  //  visible without relying on animation callbacks that may not fire
  //  consistently under StrictMode.
  // ------------------------------------------------------------------
  useEffect(() => {
    const currentEmoji = sportsEmojis[currentEmojiIndex]
    const assetReady =
      imageLoaded.has(currentEmojiIndex) || imageErrors.has(currentEmojiIndex) || !currentEmoji?.isImage

    if (!(assetReady && currentEmoji)) return

    if (lastProcessedEmojiRef.current === currentEmojiIndex) return

    const timer = setTimeout(() => {
      lastProcessedEmojiRef.current = currentEmojiIndex

      setCurrentSportName(currentEmoji.name)

      const lg = leagueMapping[currentEmoji.name] ?? "Sports"
      if (lg !== headlineLeagueRef.current) {
        setHeadlineLeague(lg)
        setHeadlineKeyword((prev) => getRandomKeyword(lg, prev))
        setHeadlineKey(Date.now())
      }
    }, 50) // control for typing delay

    return () => clearTimeout(timer)
  }, [currentEmojiIndex, imageLoaded, imageErrors])

  // Listen for navigation events and close bracket cards when user navigates away
  useEffect(() => {
    const handleCloseAllBracketCards = () => {
      setExpandedCard(null)
      setAllowBracketInteractions(false)
    }

    const handleBracketNavClicked = () => {
      setAllowBracketInteractions(true)
    }

    window.addEventListener("closeAllBracketCards", handleCloseAllBracketCards)
    window.addEventListener("bracketNavClicked", handleBracketNavClicked)

    return () => {
      window.removeEventListener("closeAllBracketCards", handleCloseAllBracketCards)
      window.removeEventListener("bracketNavClicked", handleBracketNavClicked)
    }
  }, [])

  // Dynamically rotate sport emoji; football stays on screen twice as long (14.4s).
  useEffect(() => {
    if (isAudioPlaying) return

    const delay = currentSportName === "football" ? 14400 : 7200

    const timeoutId = window.setTimeout(() => {
      setCurrentEmojiIndex((prev) => (prev + 1) % sportsEmojis.length)
    }, delay)

    return () => clearTimeout(timeoutId)
  }, [currentSportName, isAudioPlaying])

  // Headline effect removed – handled inside <LandingBracketHeadline>

  /**
   * Play football fight-song themes sequentially. Automatically advances to the next
   * track when the current one ends until all three tracks have been played (then stops).
   */
  const playFightSongSequence = (startIndex = 0) => {
    // Ensure we keep the football emoji visible
    const footballIdx = sportsEmojis.findIndex((s) => s.name === "football")
    if (footballIdx !== -1) setCurrentEmojiIndex(footballIdx)

    const clipSrc = fightSongTracks[startIndex % fightSongTracks.length]
    if (!clipSrc) return
    playSingleAudio(clipSrc, 0.9)
    setIsAudioPlaying(true)

    if (window.activeCallAudio) {
      window.activeCallAudio.addEventListener(
        "ended",
        () => {
          const nextIndex = startIndex + 1
          if (nextIndex < fightSongTracks.length) {
            // Continue to next theme in sequence
            playFightSongSequence(nextIndex)
          } else {
            // Sequence finished - hide mute button and resume emoji cycling
            setIsAudioPlaying(false)
          }
        },
        { once: true },
      )
    } else {
      // Fallback: If no active audio element, reset after a reasonable duration
      setTimeout(() => {
        setIsAudioPlaying(false)
      }, 30000) // 30 seconds fallback
    }
  }

  const handleEmojiClick = () => {
    // Determine which sport is currently displayed
    const sport = currentSportName

    // Special handling for basketball, golf, tennis, hockey, baseball, and football: play intro then moment
    if (
      sport === "basketball" ||
      sport === "golf" ||
      sport === "tennis" ||
      sport === "hockey" ||
      sport === "baseball" ||
      sport === "football"
    ) {
      // Trigger camera flash and caret cluster effects immediately
      window.dispatchEvent(new Event("sportsEmojiClick"))

      // Play the appropriate intro clip first
      const introClip =
        sport === "basketball"
          ? introBasketballClip
          : sport === "golf"
            ? introGolfClip
            : sport === "tennis"
              ? introTennisClip
              : sport === "hockey"
                ? introHockeyClip
                : sport === "baseball"
                  ? introBaseballClip
                  : introFootballClip

      playSingleAudio(introClip, 0.9)
      setIsAudioPlaying(true)

      if (window.activeCallAudio) {
        const introAudio = window.activeCallAudio
        const handleIntroEnded = () => {
          // After intro ends, play a random moment for the sport
          const momentSrc = getClipForSport(sport)
          if (!momentSrc) {
            setIsAudioPlaying(false)
            return
          }

          // Special handling for football: play fight song sequence instead of single moment
          if (sport === "football") {
            playFightSongSequence(0)
          } else {
            playSingleAudio(momentSrc, 0.9)

            if (window.activeCallAudio) {
              const momentAudio = window.activeCallAudio
              const handleMomentEnded = () => {
                setIsAudioPlaying(false)
                momentAudio.removeEventListener("ended", handleMomentEnded)
              }
              momentAudio.addEventListener("ended", handleMomentEnded)
            } else {
              // Fallback reset
              setTimeout(() => setIsAudioPlaying(false), 30000)
            }
          }

          introAudio.removeEventListener("ended", handleIntroEnded)
        }
        introAudio.addEventListener("ended", handleIntroEnded)
      } else {
        // If activeCallAudio wasn't set for some reason, stop after reasonable duration
        setTimeout(() => setIsAudioPlaying(false), 30000)
      }

      return
    }

    const clipSrc = getClipForSport(sport)
    if (!clipSrc) return

    // Trigger camera flash and caret cluster effects when sound plays
    window.dispatchEvent(new Event("sportsEmojiClick"))

    // Lock football when a fight-song is selected (e.g. default fall-back clips)
    if (fightSongTracks.includes(clipSrc)) {
      const footballIndex = sportsEmojis.findIndex((s) => s.name === "football")
      if (footballIndex !== -1) {
        setCurrentEmojiIndex(footballIndex)
      }
    }

    playSingleAudio(clipSrc, 0.9)
    setIsAudioPlaying(true)

    if (window.activeCallAudio) {
      window.activeCallAudio.addEventListener("ended", () => setIsAudioPlaying(false), { once: true })
    }
  }

  // Listen for the global event sent from the hero section to start music mode automatically
  useEffect(() => {
    const handleActivateMusicMode = () => {
      // Only trigger if music isn't already playing to avoid restarting tracks
      if (!isAudioPlaying) {
        // Force football emoji and play fight song sequence when triggered from hero
        playFightSongSequence(0)
      }
    }
    window.addEventListener("activateMusicMode", handleActivateMusicMode)
    return () => window.removeEventListener("activateMusicMode", handleActivateMusicMode)
  }, [isAudioPlaying])

  // Stop all playing audio and exit music mode
  const handleStopAudio = () => {
    if (window.activeCallAudio) {
      window.activeCallAudio.pause()
      try {
        window.activeCallAudio.currentTime = 0
      } catch (_) {
        /* ignore if not supported */
      }
      ;(window as { activeCallAudio?: HTMLAudioElement | undefined }).activeCallAudio = undefined
    }
    setIsAudioPlaying(false)
  }

  // Consolidated intersection observer for all bracket section visibility needs
  useEffect(() => {
    if (typeof window === "undefined") return

    const logoElement = document.getElementById("bracket-logo")
    const sectionEl = document.getElementById("bracket")

    if (!(logoElement && sectionEl)) return

    // Single observer for logo visibility (for background effects)
    const logoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          window.dispatchEvent(
            new CustomEvent("bracketLogoVisibility", {
              detail: { isVisible: entry.isIntersecting },
            }),
          )
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" },
    )

    // Single observer for section visibility that handles multiple concerns
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        // Treat the section as "visible" only when a significant portion (≥25%) is on-screen.
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.25

        // Background lights control
        window.dispatchEvent(new CustomEvent("bracketSectionVisible", { detail: { isVisible } }))

        // Update local visibility state so we can pause heavy animations when off-screen
        setIsBracketSectionVisible(isVisible)

        // Organic scrolling detection for bracket interactions
        if (isVisible) {
          const navClicked = sessionStorage.getItem("bracket:nav-clicked")
          if (!navClicked) {
            setAllowBracketInteractions(true)
          }
          // No automatic reopening of cards – respect user choice
        }
      },
      {
        threshold: [0.1, 0.3], // Multiple thresholds for different behaviors
        rootMargin: "0px 0px -20% 0px",
      },
    )

    logoObserver.observe(logoElement)
    sectionObserver.observe(sectionEl)

    return () => {
      logoObserver.disconnect()
      sectionObserver.disconnect()
    }
  }, [])

  // Cleanup global audio on unmount
  useEffect(() => {
    return () => {
      if (window.activeCallAudio) {
        window.activeCallAudio.pause()
        ;(window as { activeCallAudio?: HTMLAudioElement | undefined }).activeCallAudio = undefined
      }
    }
  }, [])

  const baseShadow = "0 3px 32px rgba(0,0,0,0.175), 0 5px 60px rgba(0,0,0,0.1)"
  const zoomShadow = "0 3px 48px rgba(0,0,0,0.175), 0 5px 90px rgba(0,0,0,0.1)"
  const hoverShadow = "0 3px 60px rgba(0,0,0,0.175), 0 5px 120px rgba(0,0,0,0.1)"

  return (
    <section
      id="bracket"
      className="relative z-30 w-full flex flex-col items-center gap-8 pt-12 pb-12 md:py-20 scroll-mt-[10vh] md:scroll-mt-[12vh] lg:scroll-mt-[10vh] tall:scroll-mt-[8vh]"
    >
      {/* Section kicker/overline - Shows "LIVE THE GAME" + "THIS FALL" across all screen sizes */}
      <div className="flex justify-center mb-[0.2rem] sm:mb-4">
        <div className="relative inline-block">
          <div className="relative z-10 pl-[.8rem] pr-4 -mb-[1.6rem] sm:-mb-8 sm:-mt-[0.2rem] flex flex-col items-center gap-0">
            {/* Desktop / tablet: "FEEL THE PULSE" + "THIS FALL" */}
            <div className="hidden sm:flex items-center gap-1.5">
              <LandingIconAnimatedActivity className="w-[.85rem] sm:w-[1.1rem] md:w-[1.15rem] md:h-[1.17rem] lg:w-[1.08rem]  xl:w-[1.08rem] lg:h-7 text-[#22FF65] relative" />
              <span className="text-[.875rem] sm:text-[1.04rem] font-normal tracking-wider text-white/70">
                FEEL THE PULSE
              </span>
              <span className="text-[#22FF65] font-semibold text-[.875rem] sm:text-[1.04rem] tracking-wider">
                4TH QT 20:25
              </span>
            </div>

            {/* Mobile: "FEEL THE PULSE" + "THIS FALL" */}
            <div className="flex items-center gap-1.5 sm:hidden">
              <LandingIconAnimatedActivity className="w-[.85rem] text-[#22FF65] relative" />
              <span className="text-white/70 text-[.875rem] font-normal tracking-wider">FEEL THE PULSE</span>
              <span className="text-[#22FF65] font-semibold text-[.875rem] tracking-wider">4TH QT 20:25</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative w-full flex flex-col items-center">
        {/* Bracket lockup logo placed between countdown and FEEL THE PULSE */}
        <div className="flex justify-center mb-[0.1rem] mt-1 md:mb-4">
          <img
            src="https://a.bracky.app/assets/brand/bracket-lockup-outline-reverse.svg"
            alt="Bracket logo"
            className="opacity-90 w-36 sm:w-40 md:w-48 lg:w-56 xl:w-[16rem] max-w-none select-none pointer-events-none"
            draggable="false"
            loading="lazy"
            decoding="async"
          />
        </div>

        <motion.div
          className="flex justify-center px-0 md:px-8 py-4 overflow-visible relative cursor-pointer"
          onClick={handleStopAudio}
        >
          <motion.div className="relative inline-block mx-auto" whileTap={{ scale: 0.99 }}>
            <motion.div
              aria-hidden="true"
              className="absolute inset-0 tracking-tighter font-extrabold leading-[0.84] select-none text-center opacity-40 pointer-events-none z-0"
              style={{
                fontSize: "clamp(6.25rem, 20vw, 21rem)",
                filter: isAudioPlaying ? "blur(13px) brightness(1.1)" : "blur(10px) brightness(0.9)",
              }}
              animate={{
                // Animate only opacity to avoid expensive repainting from filter changes
                opacity: [0.4, 0.6, 0.4],
              }}
              transition={{
                duration: isAudioPlaying ? 0.8 : 3.2,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              }}
            >
              <span className="relative inline-flex items-center justify-center">
                <span className="inline-block h-[0.9em] w-auto">
                  <LandingLogoLiveTheGame
                    className="inline-block h-[1.45em] w-auto cursor-pointer"
                    videoSrc={currentVideoSrc ?? undefined}
                    onClick={(e) => {
                      e.stopPropagation()
                      // If football is showing and music is playing, toggle pause/play
                      if (currentSportName === "football" && isAudioPlaying) {
                        handleStopAudio()
                      } else {
                        handleEmojiClick()
                      }
                    }}
                  />
                </span>
                <div className="absolute left-1/2 top-[70%] -translate-x-1/2 -translate-y-1/2 z-0">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentEmojiIndex}
                      className="relative"
                      style={{ fontSize: "0.7em", textShadow: isAudioPlaying ? zoomShadow : baseShadow }}
                      initial={{ scale: 0, rotate: -180, opacity: 0 }}
                      animate={{
                        scale: isAudioPlaying ? 0.99 : 0.891,
                        rotate: 0,
                        opacity: 0.6,
                        y: [0, -3, 0],
                        // Add pulse glow effect when audio is playing
                        filter: isAudioPlaying
                          ? [
                              "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
                              "brightness(1.1575) drop-shadow(0 0 30px rgba(255,248,240,0.6))",
                              "brightness(1.0975) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
                              "brightness(1.1725) drop-shadow(0 0 35px rgba(255,248,240,0.675))",
                              "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
                            ]
                          : "brightness(1) drop-shadow(0 0 0px rgba(255,248,240,0))",
                      }}
                      exit={{ scale: 0, rotate: 90, opacity: 0 }}
                      transition={{
                        duration: 0.3,
                        y: {
                          duration: bobbingDuration,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: "reverse",
                          ease: "easeInOut",
                        },
                        filter: isAudioPlaying
                          ? {
                              duration: 0.6,
                              repeat: Number.POSITIVE_INFINITY,
                              repeatType: "loop",
                              ease: "easeInOut",
                            }
                          : { duration: 0.3 },
                      }}
                      whileTap={{ scale: 0.918 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEmojiClick()
                      }}
                    >
                      {sportsEmojis[currentEmojiIndex]?.isImage ? (
                        imageErrors.has(currentEmojiIndex) ? (
                          // Fallback to regular emoji if image fails to load
                          <span style={{ fontSize: "1em" }}>
                            {currentSportName === "football"
                              ? "🏈"
                              : currentSportName === "basketball"
                                ? "🏀"
                                : currentSportName === "soccer"
                                  ? "⚽"
                                  : currentSportName === "golf"
                                    ? "⛳"
                                    : currentSportName === "tennis"
                                      ? "🎾"
                                      : currentSportName === "baseball"
                                        ? "⚾"
                                        : currentSportName === "hockey"
                                          ? "🏒"
                                          : currentSportName === "boxing"
                                            ? "🥊"
                                            : "🏈"}
                          </span>
                        ) : (
                          <img
                            src={sportsEmojis[currentEmojiIndex]?.emoji ?? ""}
                            alt={currentSportName}
                            style={{
                              width: "1em",
                              height: "1em",
                              objectFit: "contain",
                              filter: !imageLoaded.has(currentEmojiIndex) ? "opacity(0.5)" : "none",
                              transition: "filter 0.3s ease-out",
                            }}
                            draggable={false}
                            onError={() => {
                              setImageErrors((prev) => new Set([...prev, currentEmojiIndex]))
                            }}
                            onLoad={() => {
                              setImageLoaded((prev) => new Set([...prev, currentEmojiIndex]))
                            }}
                          />
                        )
                      ) : (
                        (sportsEmojis[currentEmojiIndex]?.emoji ?? "")
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </span>
            </motion.div>

            <h1
              className="tracking-tighter font-extrabold leading-[0.84] select-none text-center relative z-0"
              style={{ fontSize: "clamp(6.25rem, 20vw, 21rem)" }}
            >
              <span className="relative inline-flex items-center justify-center">
                <span className="inline-block h-[0.9em] w-auto text-white relative z-0">
                  <LandingLogoLiveTheGame
                    className="inline-block h-[1.45em] w-auto cursor-pointer"
                    videoSrc={currentVideoSrc ?? undefined}
                    onClick={(e) => {
                      e.stopPropagation()
                      // If football is showing and music is playing, toggle pause/play
                      if (currentSportName === "football" && isAudioPlaying) {
                        handleStopAudio()
                      } else {
                        handleEmojiClick()
                      }
                    }}
                  />
                </span>
                {/* Football glow effect while music is playing can DELETE later if music is removed */}
                <div className="absolute left-1/2 top-[70%] blur-[0px] -translate-x-1/2 -translate-y-1/2 z-20">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentEmojiIndex}
                      className="cursor-pointer relative"
                      style={{ fontSize: "0.7em", textShadow: isAudioPlaying ? zoomShadow : baseShadow }}
                      initial={{ scale: 0, rotate: -180, opacity: 0 }}
                      animate={{
                        scale: isAudioPlaying ? 0.963 : 0.9,
                        rotate: 0,
                        opacity: 1,
                        y: [0, -4, 0],
                        // Add pulse glow effect when audio is playing
                        filter: isAudioPlaying
                          ? [
                              "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
                              "brightness(1.1575) drop-shadow(0 0 30px rgba(255,248,240,0.6))",
                              "brightness(1.0975) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
                              "brightness(1.1725) drop-shadow(0 0 35px rgba(255,248,240,0.675))",
                              "brightness(1.0825) drop-shadow(0 0 20px rgba(255,248,240,0.45))",
                            ]
                          : "brightness(1) drop-shadow(0 0 0px rgba(255,248,240,0))",
                      }}
                      exit={{ scale: 0, rotate: 90, opacity: 0 }}
                      transition={{
                        scale: { duration: 0.2, type: "spring", bounce: 0.2 },
                        rotate: { duration: 0.2, type: "spring", bounce: 0.2 },
                        opacity: { duration: 0.3 },
                        y: {
                          duration: bobbingDuration,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: "reverse",
                          ease: "easeInOut",
                        },
                        filter: isAudioPlaying
                          ? {
                              duration: 0.6,
                              repeat: Number.POSITIVE_INFINITY,
                              repeatType: "loop",
                              ease: "easeInOut",
                            }
                          : { duration: 0.3 },
                      }}
                      whileHover={{
                        scale: 0.945,
                        rotate: 10,
                        textShadow: hoverShadow,
                        filter: "brightness(1.075) drop-shadow(0 0 25px rgba(255,248,240,0.525))",
                      }}
                      whileTap={{ scale: 0.918 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        // If football is showing and music is playing, toggle pause/play
                        if (currentSportName === "football" && isAudioPlaying) {
                          handleStopAudio()
                        } else {
                          handleEmojiClick()
                        }
                      }}
                    >
                      {sportsEmojis[currentEmojiIndex]?.isImage ? (
                        imageErrors.has(currentEmojiIndex) ? (
                          // Fallback to regular emoji if image fails to load
                          <span style={{ fontSize: "1em" }}>
                            {currentSportName === "football"
                              ? "🏈"
                              : currentSportName === "basketball"
                                ? "🏀"
                                : currentSportName === "soccer"
                                  ? "⚽"
                                  : currentSportName === "golf"
                                    ? "⛳"
                                    : currentSportName === "tennis"
                                      ? "🎾"
                                      : currentSportName === "baseball"
                                        ? "⚾"
                                        : currentSportName === "hockey"
                                          ? "🏒"
                                          : currentSportName === "boxing"
                                            ? "🥊"
                                            : "🏈"}
                          </span>
                        ) : (
                          <img
                            src={sportsEmojis[currentEmojiIndex]?.emoji ?? ""}
                            alt={currentSportName}
                            style={{
                              width: "1em",
                              height: "1em",
                              objectFit: "contain",
                              filter: !imageLoaded.has(currentEmojiIndex) ? "opacity(0.5)" : "none",
                              transition: "filter 0.3s ease-out",
                            }}
                            draggable={false}
                            onError={() => {
                              setImageErrors((prev) => new Set([...prev, currentEmojiIndex]))
                            }}
                            onLoad={() => {
                              setImageLoaded((prev) => new Set([...prev, currentEmojiIndex]))
                            }}
                          />
                        )
                      ) : (
                        (sportsEmojis[currentEmojiIndex]?.emoji ?? "")
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </span>
              <span className="bg-linear-to-r from-[#30DFFF] to-[#2992FB]  bg-clip-text text-transparent text-[0.3em] leading-none" />
            </h1>
          </motion.div>
        </motion.div>

        {/* Mute button - only shown when music is playing */}
        <AnimatePresence>
          {isAudioPlaying && (
            <motion.div
              className="absolute inset-x-0 flex justify-center w-full"
              style={{ top: "100%", marginTop: "-0.8rem" }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <button
                onClick={handleStopAudio}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white/80 hover:text-white transition-all duration-150 text-sm font-medium backdrop-blur-xs"
                type="button"
              >
                <VolumeX className="w-4 h-4" />
                Mute
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="-mt-[0.8rem] sm:-mt-4 md:mt-0 mdlg:mt-10 lg:mt-16 xl:mt-[7.1rem] 2xl:mt-[8.1rem] mb-0 sm:mb-24 md:mb-20 lg:mb-4 xl:mb-12 min-h-32 md:min-h-48 flex items-center w-full">
        <LandingBracketSectionCoverMarquee />
      </div>

      <div className="flex flex-col items-center gap-6 -mt-[3.1rem] sm:-mt-36 md:-mt-[8.6rem] mdlg:-mt-30 lg:-mt-12 xl:-mt-[5.6rem]">
        <h2
          className="bg-linear-to-b from-white to-[#dce3fe] bg-clip-text text-transparent font-extrabold leading-none text-center text-h2 px-4 pb-[0.3em]"
          style={{ letterSpacing: "-0.1rem" }}
        >
          <BracketHeadline leagueLabel={headlineLeague} keyword={headlineKeyword} animationKey={headlineKey} />
        </h2>
        <p className="text-center text-white/70 text-base sm:text-lg max-w-2xl mx-auto px-4 -mt-4 -mb-2 relative z-50 leading-[1.43]">
          <span className="text-white font-semibold">Bet</span> when no one believes,{" "}
          <span className="text-white font-semibold">sell</span> when momentum shifts.{" "}
          <br className="hidden sm:block" />
          <span className="text-white/90 font-semibold">Fans Drive The Odds. </span>
          <span className="text-white/90">Winners </span>
          <span className="text-white/90">Stack</span>{" "}
          <span className="bg-linear-to-r from-[#0074FF] to-[#30DDFF] bg-clip-text text-transparent font-semibold whitespace-nowrap">
            $BRACKY
          </span>
        </p>
        <div className="text-center relative z-999 -mt-2">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              const faqId = HOW_BRACKETS_WORK_FAQ_ID
              // Try to scroll directly to the specific FAQ card first
              const el = document.getElementById(`faq-${faqId}`)
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" })
              } else {
                // Fallback: scroll to the FAQ section itself
                document.getElementById("faqs")?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              // Open the FAQ card
              window.dispatchEvent(new CustomEvent("openFAQ", { detail: { faqId } }))
            }}
            className="text-[#22FF65] underline decoration-[#22FF65] ml-[.8rem] font-medium text-base sm:text-lg inline-flex items-center gap-[0.09rem] cursor-pointer relative z-999 min-h-[44px] py-2 px-2 touch-manipulation bg-transparent border-none pointer-events-auto"
            style={{ WebkitTapHighlightColor: "transparent" }}
          >
            How it Works
            <ArrowDown className="w-4 h-4 translate-y-[0.025rem]" />
          </button>
        </div>
      </div>

      {/* Removed stray empty div and prevent overlap by removing negative margin */}
      <div className="w-full flex flex-col items-center gap-2 -mt-12 mb-0 relative pointer-events-auto">{false}</div>
    </section>
  )
}
